import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import {
  IGroupActionContentBase,
  TGroupActionsLocale,
} from "@rentiohq/shared-frontend/dist/types/groupAction.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { TextField } from "@rentiohq/web-shared/dist/components";
import {
  stateToHTML,
  Editor,
  EditorState,
  ContentState,
} from "@rentiohq/web-shared/dist/components/Editor";
import { isEmpty } from "lodash";
import React from "react";
import { getLocaleKey } from "../GroupActions.util";
import { EGroupActionType } from "../GroupActionsModal.types";

export interface IAddGroupActionContentWithEditorSectionProps {
  type: EGroupActionType;
  index: number;
  locale: TGroupActionsLocale;
  initialContent: IGroupActionContentBase;
  children?: React.ReactNode;
  onChange: (content: IGroupActionContentBase) => void;
  onChangeTitle?: (key: TGroupActionsLocale, value?: string) => void;
  onChangeSubject?: (key: TGroupActionsLocale, value?: string) => void;
  onChangeError?: (error?: string) => void;
}

const AddGroupActionContentWithEditorSection: React.FC<
  IAddGroupActionContentWithEditorSectionProps
> = ({
  type,
  index,
  locale,
  initialContent,
  onChange,
  onChangeTitle,
  onChangeSubject,
  onChangeError,
}) => {
  const [editorState, setEditorState] = React.useState<EditorState>(() => {
    const contentState = ContentState.createFromText(
      initialContent.value || "",
    );
    return EditorState.createWithContent(contentState);
  });

  const [subject, setSubject] = React.useState("");
  const [subjectError, setSubjectError] = React.useState<string>();
  const [contentError, setContentError] = React.useState<string>();

  // Seeds the parent once, with whatever was passed in. It used to run on
  // every plain-text change and write that PLAIN text over the HTML that
  // handleContentChange had just written, because an effect runs after the
  // event handler. The format that left this modal therefore depended on
  // whether the broker's last action changed any characters: typing sent
  // plain text, a bold on an unchanged word sent HTML (DEV-3323).
  React.useEffect(() => {
    onChange({ ...initialContent, value: initialContent.value || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => onChangeSubject?.(locale, subject), [subject]);
  React.useEffect(() => {
    onChangeError?.(subjectError || contentError);
  }, [subjectError, contentError]);

  const getMaxTextLength = () => {
    if (type === EGroupActionType.Sms) return 160;
  };

  const handleContentChange = (state: EditorState) => {
    setEditorState(state);
    const contentState = state.getCurrentContent();
    const plain = contentState.getPlainText("").trim();
    const html = stateToHTML(contentState);
    onChange({
      ...initialContent,
      value: html,
    });

    if (isEmpty(plain)) {
      setContentError(`The ${initialContent.label} is required`);
    } else {
      setContentError(undefined);
    }
  };

  const renderChildren = () => {
    const lowerCaseType = type.toLowerCase();

    if (type === EGroupActionType.Email) {
      return (
        <>
          <TextField
            name={`group_actions_modal_${lowerCaseType}_subject_${locale}`}
            label={getLocalizedText(
              `group_actions.modal.subject.label.${getLocaleKey(locale)}`,
            )}
            value={subject}
            error={subjectError}
            type="input"
            onChange={(
              e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>,
            ) => {
              const { value } = e.target;

              if (isEmpty(value)) {
                setSubjectError(
                  `The ${getLocalizedText(
                    `group_actions.modal.subject.label.${getLocaleKey(locale)}`,
                  )} is required`,
                );
              } else {
                setSubjectError(undefined);
              }

              setSubject(value);
            }}
          />
          <Spacer weight={ESpacerWeight.W08} />
        </>
      );
    }

    return null;
  };

  return (
    <>
      {index > 0 && <Spacer weight={ESpacerWeight.W32} />}
      {renderChildren()}
      <Editor
        editorState={editorState}
        onChange={handleContentChange}
        spellCheck
        label={initialContent?.label}
        maxLength={getMaxTextLength()}
        error={contentError}
      />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default AddGroupActionContentWithEditorSection;
